import {useCallback} from 'react';
import {useTranslation} from 'react-i18next';

import type {
    ChatActivityMessageParticipants,
    ChatActivityType,
    ChatMessage,
} from '../types';
import {getGroupActivityMessages} from '../utils/getGroupActivityMessages';

export const useBuildTranslatedActivityChatMessages = () => {
    const {t} = useTranslation();

    const translateGroupedMessages = useCallback(
        (
            groupedParticipants: ChatActivityMessageParticipants[],
            chatActivityType: ChatActivityType,
        ) => {
            switch (groupedParticipants.length) {
                case 0:
                case 1: {
                    return;
                }
                case 2: {
                    switch (chatActivityType) {
                        case 'joined':
                            return t('meeting.two-participants-joined-group', {
                                defaultValue: '{{second}} and {{first}} joined',
                                first: groupedParticipants.at(0)?.displayName,
                                second: groupedParticipants.at(1)?.displayName,
                            });
                        case 'left':
                            return t('meeting.two-participants-left-group', {
                                defaultValue: '{{second}} and {{first}} left',
                                first: groupedParticipants.at(0)?.displayName,
                                second: groupedParticipants.at(1)?.displayName,
                            });
                        default:
                            throw new Error(
                                `Unhandled activity type: ${chatActivityType}`,
                            );
                    }
                }
                case 3: {
                    switch (chatActivityType) {
                        case 'joined':
                            return t(
                                'meeting.three-participants-joined-group',
                                {
                                    defaultValue:
                                        '{{third}}, {{second}} and {{first}} joined',
                                    first: groupedParticipants.at(0)
                                        ?.displayName,
                                    second: groupedParticipants.at(1)
                                        ?.displayName,
                                    third: groupedParticipants.at(2)
                                        ?.displayName,
                                },
                            );
                        case 'left':
                            return t('meeting.three-participants-left-group', {
                                defaultValue:
                                    '{{third}}, {{second}} and {{first}} left',
                                first: groupedParticipants.at(0)?.displayName,
                                second: groupedParticipants.at(1)?.displayName,
                                third: groupedParticipants.at(2)?.displayName,
                            });
                        default:
                            throw new Error(
                                `Unhandled activity type: ${chatActivityType}`,
                            );
                    }
                }
                case 4: {
                    switch (chatActivityType) {
                        case 'joined':
                            return t('meeting.four-participants-joined-group', {
                                defaultValue:
                                    '{{third}}, {{second}}, {{first}} and 1 other joined',
                                first: groupedParticipants.at(1)?.displayName,
                                second: groupedParticipants.at(2)?.displayName,
                                third: groupedParticipants.at(3)?.displayName,
                            });
                        case 'left':
                            return t('meeting.four-participants-left-group', {
                                defaultValue:
                                    '{{third}}, {{second}}, {{first}} and 1 other left',
                                first: groupedParticipants.at(1)?.displayName,
                                second: groupedParticipants.at(2)?.displayName,
                                third: groupedParticipants.at(3)?.displayName,
                            });
                        default:
                            throw new Error(
                                `Unhandled activity type: ${chatActivityType}`,
                            );
                    }
                }
                default: {
                    switch (chatActivityType) {
                        case 'joined':
                            return t(
                                'meeting.more-than-four-participants-joined-group',
                                {
                                    defaultValue:
                                        '{{third}}, {{second}}, {{first}} and {{remaining}} others joined',
                                    first: groupedParticipants.at(-3)
                                        ?.displayName,
                                    second: groupedParticipants.at(-2)
                                        ?.displayName,
                                    third: groupedParticipants.at(-1)
                                        ?.displayName,
                                    remaining: groupedParticipants.length - 3,
                                },
                            );
                        case 'left':
                            return t(
                                'meeting.more-than-four-participants-left-group',
                                {
                                    defaultValue:
                                        '{{third}}, {{second}}, {{first}} and {{remaining}} others left',
                                    first: groupedParticipants.at(-3)
                                        ?.displayName,
                                    second: groupedParticipants.at(-2)
                                        ?.displayName,
                                    third: groupedParticipants.at(-1)
                                        ?.displayName,
                                    remaining: groupedParticipants.length - 3,
                                },
                            );
                        default:
                            throw new Error(
                                `Unhandled activity type: ${chatActivityType}`,
                            );
                    }
                }
            }
        },
        [t],
    );

    const updateMessage = useCallback(
        (prevMessages: ChatMessage[], chatActivityMessage?: ChatMessage) => {
            if (chatActivityMessage) {
                const groupActivityMessages = getGroupActivityMessages(
                    prevMessages,
                    chatActivityMessage,
                );
                const groupedMessage = groupActivityMessages.at(-1);

                if (
                    groupedMessage?.groupedParticipants?.length &&
                    groupedMessage.activityType
                ) {
                    const message = translateGroupedMessages(
                        groupedMessage.groupedParticipants,
                        groupedMessage.activityType,
                    );
                    if (message) {
                        groupedMessage.message = message;
                    }
                }
                return groupActivityMessages;
            }
            return prevMessages;
        },
        [translateGroupedMessages],
    );

    return updateMessage;
};
